import * as React from 'react'
import { ActivityToEventMappingsConfigurationModel } from '@/lib/api/company/configuration/timesheet-additional/types'
import { ActivityToMapType } from '@/app/constants/configuration/activityToMapType'
import { MappingActivityDestination } from '@/app/constants/configuration/mappingActivityDestination'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { Checkbox } from '@/ui'
import TableTextHeader from '@/modules/drivers/shared/tableItems/TableTextHeader'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'

type Option<T extends number> = { value: T; label: string }

type Props = {
  rows: ActivityToEventMappingsConfigurationModel[]
  setRows: React.Dispatch<
    React.SetStateAction<ActivityToEventMappingsConfigurationModel[]>
  >
  activityTypeOptions: Option<ActivityToMapType>[]
  destinationOptions: Option<MappingActivityDestination>[]
}

type RowPatch = Partial<
  Pick<
    ActivityToEventMappingsConfigurationModel,
    | 'activityToMapType'
    | 'timeFrom'
    | 'timeTo'
    | 'mappingActivityDestination'
    | 'splitActivity'
    | 'ignoreRests'
    | 'isNew'
  >
>

// ---------- helpers ----------
function parseTimeToMinutes(value: string | undefined | null): number | null {
  if (!value) return null
  const m = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.exec(value)
  if (!m) return null
  return Number(m[1]) * 60 + Number(m[2])
}

function stripSeconds(value: string | undefined | null): string {
  if (!value) return ''
  const m = /^(\d{2}:\d{2})(?::\d{2})?$/.exec(value)
  return m ? m[1] : value
}


function sanitizeTimeInput(value: string): string {
  const digitsOnly = value.replace(/[^\d]/g, '').slice(0, 4)
  if (digitsOnly.length === 0) return ''
  let hh = digitsOnly.slice(0, 2)
  const mm = digitsOnly.slice(2, 4)
  if (hh.length === 2) {
    const h = Math.min(23, Number(hh))
    hh = String(h).padStart(2, '0')
  }
  if (mm.length === 0) return hh
  let mmOut = mm
  if (mm.length === 2) {
    const m = Math.min(59, Number(mm))
    mmOut = String(m).padStart(2, '0')
  }
  return `${hh}:${mmOut}`
}

function minutesOverlap(aFrom: number, aTo: number, bFrom: number, bTo: number) {
  return aFrom < bTo && bFrom < aTo
}

function normalizeOrderIndex(
  arr: ActivityToEventMappingsConfigurationModel[],
): ActivityToEventMappingsConfigurationModel[] {
  return arr.map((r, idx) => ({ ...r, orderIndex: idx }))
}

// ---------- component ----------
export const EventQualificationTable = ({
  rows,
  setRows,
  activityTypeOptions,
  destinationOptions,
}: Props) => {
  const { t } = useLocalize()

  const [touchedRows, setTouchedRows] = React.useState<Set<number>>(new Set())

  function markTouched(index: number) {
    setTouchedRows((prev) => {
      if (prev.has(index)) return prev
      const next = new Set(prev)
      next.add(index)
      return next
    })
  }

  function updateRow(index: number, patch: RowPatch) {
    setRows((prev) => {
      const copy = [...prev]
      copy[index] = { ...copy[index], ...patch }
      const next = copy[index]
      const nextIgnoreEnabled =
        next.activityToMapType === ActivityToMapType.Rest &&
        (parseTimeToMinutes(String(next.timeTo ?? '')) ?? 0) >= 180
      if (!nextIgnoreEnabled) {
        copy[index] = { ...copy[index], ignoreRests: false }
      }
      return normalizeOrderIndex(copy)
    })
  }

  function addRow() {
    setRows((prev) =>
      normalizeOrderIndex([
        ...prev,
        {
          isNew: true,
          activityToMapType: ActivityToMapType.Rest,
          splitActivity: false,
          timeFrom: '00:00',
          ignoreRests: false,
          mappingActivityDestination: MappingActivityDestination.Duty50,
          orderIndex:
            rows.length === 0
              ? 1
              : Math.max(...rows.map((r) => r.orderIndex)) + 1,
        },
      ]),
    )
  }

  function removeRow(index: number) {
    setRows((prev) => normalizeOrderIndex(prev.filter((_, i) => i !== index)))
  }

  function moveUp(index: number) {
    if (index <= 0) return
    setRows((prev) => {
      const copy = [...prev]
      ;[copy[index - 1], copy[index]] = [copy[index], copy[index - 1]]
      return normalizeOrderIndex(copy)
    })
  }

  function moveDown(index: number) {
    setRows((prev) => {
      if (index >= prev.length - 1) return prev
      const copy = [...prev]
      ;[copy[index], copy[index + 1]] = [copy[index + 1], copy[index]]
      return normalizeOrderIndex(copy)
    })
  }

  // ---- validation ----
  const rowErrors = React.useMemo(() => {
    const errors: Record<number, string[]> = {}

    rows.forEach((r, idx) => {
      const fromMin = parseTimeToMinutes(String(r.timeFrom ?? ''))
      const toMin = parseTimeToMinutes(String(r.timeTo ?? ''))
      const msgs: string[] = []
      if (fromMin === null) {
        msgs.push(t('configuration-timesheet.invalid-time-format'))
      } else if (toMin && fromMin > toMin) {
        msgs.push(t('configuration-timesheet.time-from-after-to'))
      }
      if (msgs.length) errors[idx] = msgs
    })

    const groups = new Map<ActivityToMapType, { idx: number; from: number; to: number }[]>()
    rows.forEach((r, idx) => {
      const fromMin = parseTimeToMinutes(String(r.timeFrom ?? ''))
      const toMin = parseTimeToMinutes(String(r.timeTo ?? ''))
      if (fromMin === null || toMin === null || fromMin > toMin) return
      const list = groups.get(r.activityToMapType) ?? []
      list.push({ idx, from: fromMin, to: toMin })
      groups.set(r.activityToMapType, list)
    })

    groups.forEach((list) => {
      const sorted = [...list].sort((a, b) => a.from - b.from || a.to - b.to)
      for (let i = 0; i < sorted.length; i++) {
        for (let j = i + 1; j < sorted.length; j++) {
          const a = sorted[i]
          const b = sorted[j]
          if (b.from >= a.to) break
          if (minutesOverlap(a.from, a.to, b.from, b.to)) {
            errors[a.idx] = [...(errors[a.idx] ?? []), t('configuration-timesheet.overlapping-times')]
            errors[b.idx] = [...(errors[b.idx] ?? []), t('configuration-timesheet.overlapping-times')]
          }
        }
      }
    })

    return errors
  }, [rows, t])

  const hasAnyErrors = Object.keys(rowErrors).some(
    (idx) => touchedRows.has(Number(idx)),
  )

  // ---- actions dropdown ----
  function RowActions({
    index,
    disabledUp,
    disabledDown,
  }: {
    index: number
    disabledUp: boolean
    disabledDown: boolean
  }) {
    const [open, setOpen] = React.useState(false)

    return (
      <div className="tw-relative tw-inline-block">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-px-2 tw-py-1 tw-text-sm tw-text-content-base hover:tw-bg-primary/10"
          aria-haspopup="menu"
          aria-expanded={open}
          title={t('common.actions')}
        >
          ⋯
        </button>

        {open && (
          <div
            className="tw-absolute tw-right-0 tw-z-10 tw-mt-1 tw-w-44 tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-lg"
            role="menu"
          >
            <button
              type="button"
              onClick={() => { setOpen(false); moveUp(index) }}
              disabled={disabledUp}
              className="tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-sm hover:tw-bg-slate-50 disabled:tw-opacity-40 disabled:hover:tw-bg-white"
              role="menuitem"
            >
              {t('configuration-timesheet.move-up')}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); moveDown(index) }}
              disabled={disabledDown}
              className="tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-sm hover:tw-bg-slate-50 disabled:tw-opacity-40 disabled:hover:tw-bg-white"
              role="menuitem"
            >
              {t('configuration-timesheet.move-down')}
            </button>
            <div className="tw-h-px tw-bg-slate-200" />
            <button
              type="button"
              onClick={() => { setOpen(false); removeRow(index) }}
              className="tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-text-red-600 hover:tw-bg-red-50"
              role="menuitem"
            >
              {t('common.delete')}
            </button>
          </div>
        )}

        {open && (
          <button
            type="button"
            className="tw-fixed tw-inset-0 tw-z-0 tw-cursor-default"
            onClick={() => setOpen(false)}
            aria-label={t('common.close')}
          />
        )}
      </div>
    )
  }

  return (
    <div className="tw-w-full">
      {/* title + add button */}
      <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">
        <div>
          <h2 className="tw-text-base tw-font-semibold tw-text-content-base">
            {t('configuration-timesheet.event-qualification')}
          </h2>
          <p className="tw-mt-0.5 tw-text-sm tw-text-content-base tw-opacity-60">
            {t('configuration-timesheet.event-qualification-description')}
          </p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-3">
          {hasAnyErrors && (
            <span className="tw-text-sm tw-text-red-600">
              {t('configuration-timesheet.validation-errors')}
            </span>
          )}
          <button
            type="button"
            onClick={addRow}
            className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-primary tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-white hover:tw-opacity-90 focus:tw-outline-none"
            title={t('common.add')}
          >
            +
          </button>
        </div>
      </div>

      {/* table */}
      <table className="tw-w-full tw-table-auto">
        <thead className="tw-w-full tw-select-none tw-border-b tw-bg-content-primary">
          <tr>
            <TableTextHeader sort={false} maxWidthOff>
              {t('configuration-timesheet.event-type')}
            </TableTextHeader>
            <TableTextHeader sort={false} maxWidthOff>
              {t('configuration-timesheet.duration-from')}
            </TableTextHeader>
            <TableTextHeader sort={false} maxWidthOff>
              {t('configuration-timesheet.duration-to')}
            </TableTextHeader>
            <TableTextHeader sort={false} maxWidthOff>
              {t('configuration-timesheet.map-to')}
            </TableTextHeader>
            <TableTextHeader sort={false} maxWidthOff>
              {t('configuration-timesheet.split-activity')}
            </TableTextHeader>
            <TableTextHeader sort={false} maxWidthOff>
              {t('configuration-timesheet.ignore-rests')}
            </TableTextHeader>
            <th className="tw-w-10" />
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={7}
                className="tw-py-8 tw-text-center tw-text-sm tw-text-content-base tw-opacity-60"
              >
                {t('configuration-timesheet.no-rules')}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const isTouched = touchedRows.has(index)
              const errs = isTouched ? rowErrors[index] ?? [] : []
              const hasErr = errs.length > 0
              const ignoreEnabled =
                row.activityToMapType === ActivityToMapType.Rest &&
                (parseTimeToMinutes(String(row.timeTo ?? '')) ?? 0) >= 180

              return (
                <React.Fragment key={index}>
                <tr
                  className={`tw-bg-white hover:tw-bg-primary/10 ${hasErr ? '' : 'tw-border-b'}`}
                >
                  {/* 1. typ zdarzenia */}
                  <td className="tw-py-2 tw-pl-2.5 tw-pr-3">
                    <FieldsetSelect
                      background
                      searchEnabled={false}
                      value={String(row.activityToMapType)}
                      options={activityTypeOptions.map((o) => ({ value: String(o.value), label: o.label }))}
                      handleChange={(val) =>
                        updateRow(index, { activityToMapType: Number(val) as ActivityToMapType })
                      }
                    />
                  </td>

                  {/* 2. długość od */}
                  <td className="tw-py-2.5 tw-pl-2.5 tw-pr-3">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={5}
                      placeholder="HH:MM"
                      pattern="^([01]\d|2[0-3]):([0-5]\d)$"
                      value={stripSeconds(String(row.timeFrom ?? ''))}
                      onChange={(e) =>
                        updateRow(index, { timeFrom: sanitizeTimeInput(e.target.value) })
                      }
                      onBlur={() => markTouched(index)}
                      className="tw-w-20 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-center tw-text-sm tw-text-content-base focus:tw-outline-none"
                    />
                  </td>

                  {/* 3. długość do */}
                  <td className="tw-py-2.5 tw-pl-2.5 tw-pr-3">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={5}
                      placeholder="HH:MM"
                      pattern="^([01]\d|2[0-3]):([0-5]\d)$"
                      value={stripSeconds(String(row.timeTo ?? ''))}
                      onChange={(e) =>
                        updateRow(index, { timeTo: sanitizeTimeInput(e.target.value) })
                      }
                      onBlur={() => markTouched(index)}
                      className="tw-w-20 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-center tw-text-sm tw-text-content-base focus:tw-outline-none"
                    />
                  </td>

                  {/* 4. zamieniaj na */}
                  <td className="tw-py-2 tw-pl-2.5 tw-pr-3">
                    <FieldsetSelect
                      background
                      searchEnabled={false}
                      value={String(row.mappingActivityDestination)}
                      options={destinationOptions.map((o) => ({ value: String(o.value), label: o.label }))}
                      handleChange={(val) =>
                        updateRow(index, { mappingActivityDestination: Number(val) as MappingActivityDestination })
                      }
                    />
                  </td>

                  {/* 5. splitActivity */}
                  <td className="tw-py-2.5 tw-pl-2.5 tw-pr-3">
                    <Checkbox
                      checked={!!row.splitActivity}
                      onChange={(e) =>
                        updateRow(index, { splitActivity: e.target.checked })
                      }
                    />
                  </td>

                  {/* 6. ignoreRests (only if Rest + timeTo >= 3h) */}
                  <td className="tw-py-2.5 tw-pl-2.5 tw-pr-3">
                    {ignoreEnabled && (
                      <Checkbox
                        checked={!!row.ignoreRests}
                        onChange={(e) =>
                          updateRow(index, { ignoreRests: e.target.checked })
                        }
                      />
                    )}
                  </td>

                  {/* 7. actions */}
                  <td className="tw-py-2.5 tw-pr-2 tw-text-right">
                    <RowActions
                      index={index}
                      disabledUp={index === 0}
                      disabledDown={index === rows.length - 1}
                    />
                  </td>
                </tr>
                {hasErr && (
                  <tr className="tw-border-b">
                    <td colSpan={7} className="tw-px-2.5 tw-py-1.5">
                      <div className="tw-space-y-0.5">
                        {errs.map((m, i) => (
                          <div key={i} className="tw-text-xs tw-text-red-700">
                            {m}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
